import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppAuth } from './useAppAuth';
import { createApiClient, deliveryEstimateService } from '../services/api';
import { VehicleType, formatDeliveryEstimate } from '../types';
import type { DeliveryEstimateResult } from '../types';

/**
 * "Delivery from £X" for one listing.
 *
 * A hook rather than a component because two very different surfaces need the same answer —
 * the listing page and checkout — and what they share is the fetch, not the markup.
 *
 * Both ends are postcodes rather than coordinates, so they are resolved through postcodes.io
 * here. That is deliberate on the browse tier: the API makes no routing call either, because
 * a metered request per listing card would exhaust the daily budget in minutes and the figure
 * is explicitly indicative.
 */
export function useDeliveryEstimate(opts: {
  listingId: string | undefined;
  /** Seller's location. Outcode-only pre-purchase — that is all a buyer may see. */
  fromPostcode: string | null | undefined;
  /** Buyer's own postcode, from their profile. */
  toPostcode: string | null | undefined;
  vehicleType?: VehicleType;
  /** False once both full postcodes are known, which drops the "~" from the label. */
  isIndicative?: boolean;
  enabled?: boolean;
}) {
  const { listingId, fromPostcode, toPostcode, vehicleType, isIndicative = true, enabled = true } = opts;
  const { getToken } = useAppAuth();

  const ready = Boolean(enabled && listingId && fromPostcode?.trim() && toPostcode?.trim());

  const { data, isLoading } = useQuery<DeliveryEstimateResult | null>({
    queryKey: ['delivery-estimate', listingId, fromPostcode, toPostcode, vehicleType],
    enabled: ready,
    staleTime: 5 * 60_000,
    // A missing estimate is not an error worth retrying — usually it means too few carriers
    // cover the route, which will not change on a second attempt a moment later.
    retry: false,
    queryFn: async () => {
      const [from, to] = await Promise.all([
        geocode(fromPostcode!),
        geocode(toPostcode!),
      ]);
      if (!from || !to) return null;

      const api = createApiClient(getToken);
      const [first] = await deliveryEstimateService(api).batch([{
        key: listingId!,
        fromLat: from.lat,
        fromLng: from.lng,
        toLat: to.lat,
        toLng: to.lng,
        vehicleType: vehicleType ?? VehicleType.LWBVan,
        isIndicative,
      }]);
      return first ?? null;
    },
  });

  const label = useMemo(() => formatDeliveryEstimate(data), [data]);

  return { estimate: data ?? null, label, isLoading: ready && isLoading };
}

/**
 * Postcode to coordinates, falling back from the full postcode to its outcode.
 *
 * Returns null rather than throwing: a buyer whose profile holds a typo should see no
 * estimate, not a broken listing page.
 */
async function geocode(postcode: string): Promise<{ lat: number; lng: number } | null> {
  const cleaned = postcode.trim().toUpperCase();
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`);
    const data = await res.json();
    if (data.result?.latitude) return { lat: data.result.latitude, lng: data.result.longitude };

    const outcode = cleaned.replace(/\s.*$/, '');
    const res2 = await fetch(`https://api.postcodes.io/outcodes/${encodeURIComponent(outcode)}`);
    const data2 = await res2.json();
    if (data2.result?.latitude) return { lat: data2.result.latitude, lng: data2.result.longitude };
  } catch {
    // postcodes.io is a free third-party service with no SLA.
  }
  return null;
}
